import {
  IconPlatformAndroid,
  IconPlatformApple,
  IconPlatformLaptop,
  IconPlatformPhone,
  IconPlatformVr,
  IconPlatformWeb,
  IconPlatformWindows
} from '../../../../../../shared/icon/icon'

// Where a course runs ("Available on", owner 2026-10-07). The keys are
// content's (api/ddd/common/coursebuild): each node in the Content and
// Templates lists carries `platforms` (these keys), and ?platform=<key> keeps
// the courses available there and the folders above them. A course is
// available on a platform when it has that platform's build (a
// <PLATFORM>_CONTENT asset) whose current version is uploaded -- the same rule
// as Launch's web check (use-course-web-builds.js).
//
// One entry per build. `label` names the platform in the filter and in
// messages, short: a native select is as wide as its longest option, so a
// long label widens the whole filter (owner 2026-10-11). `covers` says what
// its build runs on, in the capsule's list. There is no Quest build of its
// own: the phone and tablet app, the Quest app, Pico and Android XR all load
// the Android build, so it is "Android" (owner 2026-10-28), and the headset
// names are shortcuts for it. The Windows build also runs PC VR headsets.
export const PLATFORMS = [
  { key: 'web', label: 'Web', covers: 'Any web browser', Icon: IconPlatformWeb, aliases: ['web', 'browser'] },
  { key: 'windows', label: 'Windows', covers: 'Desktop and PC VR', Icon: IconPlatformWindows, aliases: ['windows', 'win', 'pc'] },
  {
    key: 'android',
    label: 'Android',
    covers: 'Phones and tablets · Quest, Pico, Android XR',
    Icon: IconPlatformAndroid,
    aliases: ['android', 'quest', 'meta', 'pico', 'vive', 'androidxr', 'vr']
  },
  { key: 'mac', label: 'macOS', covers: 'Mac', Icon: IconPlatformLaptop, aliases: ['mac', 'macos', 'osx'] },
  { key: 'ios', label: 'iOS', covers: 'iPhone and iPad', Icon: IconPlatformPhone, aliases: ['ios', 'iphone', 'ipad'] },
  { key: 'visionos', label: 'visionOS', covers: 'Apple Vision Pro', Icon: IconPlatformVr, aliases: ['visionos', 'vision', 'avp'] }
]

// The card's capsule (owner 2026-10-28, option B with the dot): one segment
// per family, in this order. Web and Windows are one icon each. Android and
// Apple are their logo, a dot, then their devices: Android's one build serves
// phones, tablets and standalone headsets, so it always shows both; Apple has
// a build per device, so a course shows only the ones it has. When the
// capsule is wider than its card, a family folds to its logo and a count
// ("+2"), lowest `foldOrder` first: Android's pair is the same on every
// course, so it says the least.
export const FAMILIES = [
  { key: 'web', hover: 'Web browser', Icon: IconPlatformWeb, devices: [{ platform: 'web' }] },
  { key: 'windows', hover: 'Windows · desktop and PC VR', Icon: IconPlatformWindows, devices: [{ platform: 'windows' }] },
  {
    key: 'android',
    name: 'Android',
    hover: 'Android · phones, tablets, Quest, Pico, Android XR',
    Icon: IconPlatformAndroid,
    foldOrder: 1,
    devices: [
      { platform: 'android', hover: 'Android phones and tablets', Icon: IconPlatformPhone },
      { platform: 'android', hover: 'Quest, Pico and Android XR headsets', Icon: IconPlatformVr }
    ]
  },
  {
    key: 'apple',
    name: 'Apple',
    Icon: IconPlatformApple,
    foldOrder: 2,
    devices: [
      { platform: 'mac', hover: 'Mac', Icon: IconPlatformLaptop },
      { platform: 'ios', hover: 'iPhone and iPad', Icon: IconPlatformPhone },
      { platform: 'visionos', hover: 'Apple Vision Pro', Icon: IconPlatformVr }
    ]
  }
]

// The families a course runs on, each with only the devices it has a build
// for. Apple's logo lists those devices on hover ("Apple · Mac, iPhone and
// iPad"); the others say what their one build covers.
export const familiesFor = platforms => {
  const has = Array.isArray(platforms) ? platforms : []
  return FAMILIES.map(f => {
    const devices = f.devices.filter(d => has.includes(d.platform))
    if (!devices.length) return null
    const hover = f.hover || `${f.name} · ${devices.map(d => d.hover).join(', ')}`
    return { ...f, devices: f.name ? devices : [], hover }
  }).filter(Boolean)
}

const BY_ALIAS = new Map(PLATFORMS.flatMap(p => p.aliases.map(alias => [alias, p.key])))

export const platformLabel = key => {
  const platform = PLATFORMS.find(p => p.key === key)
  return platform ? platform.label : ''
}

// The search box's shortcut: "web:" (any case, anywhere in the text, with
// or without a space after it) picks that platform; the rest of the text is
// the search. { platform: null } when there is none. Whitespace after a word
// that isn't a platform stays put, so "plus: web:" still finds "web:".
const SHORTCUT = /(^|\s)([a-z]+):/gi

export const takePlatformShortcut = value => {
  const text = value || ''
  SHORTCUT.lastIndex = 0
  let match
  while ((match = SHORTCUT.exec(text))) {
    const platform = BY_ALIAS.get(match[2].toLowerCase())
    if (platform) {
      const start = match.index + match[1].length
      let end = match.index + match[0].length
      while (end < text.length && /\s/.test(text[end])) end++
      return { platform, text: text.slice(0, start) + text.slice(end) }
    }
  }
  return { platform: null, text }
}
